import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Nav, Tab, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileContract, 
  faSignature, 
  faCheckCircle, 
  faDownload,
  faClock,
  faExclamationTriangle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';
import './CustomerDocuments.scss';

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'pending' | 'completed' | 'expired';
  created_at: string;
  signed_at?: string;
  expires_at?: string;
  description?: string;
  urgency?: 'high' | 'medium' | 'low';
}

const CustomerDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/customer-portal/documents');
      if (response.data.success) {
        setDocuments(response.data.documents);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const openSigningFlow = (documentId: string) => {
    // Navigate to signing flow or open modal
    window.location.href = `/customer-portal/sign/${documentId}`;
  };

  const downloadDocument = async (documentId: string) => {
    try {
      const response = await axiosInstance.get(`/customer-portal/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${documentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const pendingDocs = documents.filter(d => d.status === 'pending');
  const completedDocs = documents.filter(d => d.status === 'completed');

  const getUrgencyBadge = (urgency?: string) => {
    switch (urgency) {
      case 'high':
        return <Badge bg="danger">Urgent</Badge>;
      case 'medium':
        return <Badge bg="warning">Important</Badge>;
      default:
        return null;
    }
  };

  const getDaysUntilExpiry = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days <= 3) {
      return <span className="text-danger">Expires in {days} days</span>;
    }
    return <span className="text-muted">Expires in {days} days</span>;
  };

  return (
    <Container className="customer-documents py-4">
        {/* Header with AI Assistant Tip */}
        <div className="documents-header mb-4">
          <h2 className="mb-3">Your Documents</h2>
          
          {pendingDocs.length > 0 && (
            <Alert variant="info" className="ai-tip">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <strong>Theta AI Tip:</strong> You have {pendingDocs.length} document{pendingDocs.length > 1 ? 's' : ''} waiting for your signature. 
                  Signing these will help move your home purchase forward!
                </div>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => openSigningFlow(pendingDocs[0].id)}
                >
                  Start Signing <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </div>
            </Alert>
          )}
        </div>

        {/* Tabs for Pending/Completed */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'pending')}>
          <Nav variant="pills" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="pending">
                To Sign
                {pendingDocs.length > 0 && (
                  <Badge bg="danger" className="ms-2">{pendingDocs.length}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="completed">
                Completed
                {completedDocs.length > 0 && (
                  <Badge bg="success" className="ms-2">{completedDocs.length}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Pending Documents */}
            <Tab.Pane eventKey="pending">
              {pendingDocs.length === 0 ? (
                <Card className="text-center py-5">
                  <Card.Body>
                    <FontAwesomeIcon 
                      icon={faCheckCircle} 
                      size="3x" 
                      className="text-success mb-3" 
                    />
                    <h5>All caught up!</h5>
                    <p className="text-muted">No documents waiting for your signature.</p>
                  </Card.Body>
                </Card>
              ) : (
                <Row>
                  {pendingDocs.map((doc) => (
                    <Col md={6} lg={4} key={doc.id} className="mb-3">
                      <Card className="document-card h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <FontAwesomeIcon 
                              icon={faFileContract} 
                              size="2x" 
                              className="text-primary" 
                            />
                            {getUrgencyBadge(doc.urgency)}
                          </div>
                          
                          <h5 className="card-title">{doc.name}</h5>
                          <p className="card-text text-muted small">
                            {doc.description || 'Legal document for your review and signature'}
                          </p>
                          
                          <div className="mb-3">
                            {getDaysUntilExpiry(doc.expires_at)}
                          </div>
                          
                          <Button 
                            variant="primary" 
                            className="w-100"
                            onClick={() => openSigningFlow(doc.id)}
                          >
                            <FontAwesomeIcon icon={faSignature} /> Sign Now
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Tab.Pane>

            {/* Completed Documents */}
            <Tab.Pane eventKey="completed">
              {completedDocs.length === 0 ? (
                <Card className="text-center py-5">
                  <Card.Body>
                    <FontAwesomeIcon 
                      icon={faFileContract} 
                      size="3x" 
                      className="text-muted mb-3" 
                    />
                    <h5>No completed documents yet</h5>
                    <p className="text-muted">Signed documents will appear here.</p>
                  </Card.Body>
                </Card>
              ) : (
                <Row>
                  {completedDocs.map((doc) => (
                    <Col md={6} lg={4} key={doc.id} className="mb-3">
                      <Card className="document-card completed h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <FontAwesomeIcon 
                              icon={faCheckCircle} 
                              size="2x" 
                              className="text-success" 
                            />
                            <Badge bg="success">Signed</Badge>
                          </div>
                          
                          <h5 className="card-title">{doc.name}</h5>
                          <p className="card-text text-muted small">
                            Signed on {new Date(doc.signed_at!).toLocaleDateString()}
                          </p>
                          
                          <Button 
                            variant="outline-primary" 
                            className="w-100"
                            onClick={() => downloadDocument(doc.id)}
                          >
                            <FontAwesomeIcon icon={faDownload} /> Download
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* Quick Actions */}
        <div className="quick-actions mt-5">
          <Card>
            <Card.Body>
              <h5>Need Help?</h5>
              <p className="mb-0">
                If you have questions about any documents, you can ask Theta AI in the chat or 
                contact your agent {/* Add agent name dynamically */} directly.
              </p>
            </Card.Body>
          </Card>
        </div>
      </Container>
  );
};

export default CustomerDocuments;